import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Tooltip } from '@/design-system';
import ChatPromptIcon from '@/icons/ChatPromptIcon.svg?react';
import HubIcon from '@/icons/HubIcon.svg?react';
import { appHubIndexPath, appPromptsIndexPath } from '@/utils/constants';

import { useOrganizationId, useSelectedTenant } from '../hooks/useSwr';

export function HubRunTag({
  hubInfo,
}: {
  hubInfo: {
    repo: string;
    owner?: string;
    commit?: string;
  };
}) {
  const organizationId = useOrganizationId();
  const { data: selectedTenant } = useSelectedTenant();

  const hubOrg = hubInfo.owner;
  const hubRepo = hubInfo.repo;
  const commitHash = hubInfo.commit;

  // if the prompt is the current tenant's and not a public hub prompt, link to the prompt tab
  const shouldLinkToPromptsPage =
    hubInfo.owner === selectedTenant?.tenant_handle ||
    !hubInfo.owner ||
    hubInfo.owner === '-';

  const { path, tooltip } = useMemo(() => {
    const shortCommit = commitHash?.slice(0, 8);

    if (commitHash) {
      const basePath = `${hubRepo}/${shortCommit}`;
      const tooltipText = `${hubRepo}:${shortCommit}`;

      return {
        path: shouldLinkToPromptsPage ? basePath : `${hubOrg}/${basePath}`,
        tooltip: shouldLinkToPromptsPage
          ? tooltipText
          : `${hubOrg}/${tooltipText}`,
      };
    }

    if (shouldLinkToPromptsPage) {
      return { path: hubRepo, tooltip: hubRepo };
    }

    // no commit hash and is public prompt
    const path = `${hubOrg}/${hubRepo}`;
    return { path, tooltip: path };
  }, [commitHash, hubOrg, hubRepo, shouldLinkToPromptsPage]);

  // todo: should we add rendering logic in the event you are not logged in
  // and it's a public hub prompt?
  if (!organizationId) return null;

  return (
    <Tooltip title={tooltip}>
      <Link
        to={`/${
          shouldLinkToPromptsPage ? appPromptsIndexPath : appHubIndexPath
        }/${path}?organizationId=${organizationId}`}
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-secondary px-1 py-0.5 text-xs focus-within:bg-secondary-hover hover:bg-secondary-hover active:bg-secondary"
        onClick={(e) => e.stopPropagation()}
      >
        {shouldLinkToPromptsPage ? (
          <ChatPromptIcon className="h-3 w-3" />
        ) : (
          <HubIcon className="h-3 w-3" />
        )}
        <span>{shouldLinkToPromptsPage ? 'Prompt' : 'Hub'}</span>
      </Link>
    </Tooltip>
  );
}
